import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import './AppsLanding.css';

interface App {
  id: string;
  name: string;
  description: string;
  icon: string;
  path: string;
  color: string;
  isExternal: boolean;
  requiredGroups?: string[]; // Array of Entra ID group names
}

const AppsLanding: React.FC = () => {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);

  // Mark that user has visited apps landing (for deep link prevention)
  useEffect(() => {
    sessionStorage.setItem('visited-apps-landing', 'true');
  }, []);

  // Load user's Entra ID groups
  useEffect(() => {
    const loadUserGroups = async () => {
      if (accounts.length > 0) {
        try {
          // Request groups information from Microsoft Graph
          const response = await instance.acquireTokenSilent({
            scopes: ['https://graph.microsoft.com/GroupMember.Read.All'],
            account: accounts[0]
          });

          // Alternative: Get groups from the account claims (if available)
          const groups = accounts[0].idTokenClaims?.groups as string[] || [];
          setUserGroups(groups);
          setGroupsLoaded(true);
        } catch (error) {
          console.error('Error loading user groups:', error);
          // Fallback: try to get groups from token claims
          const groups = accounts[0].idTokenClaims?.groups as string[] || [];
          setUserGroups(groups);
          setGroupsLoaded(true);
        }
      }
    };

    loadUserGroups();
  }, [accounts, instance]);

  const handleSignOut = () => {
    sessionStorage.removeItem('visited-apps-landing');
    instance.logoutPopup({
      postLogoutRedirectUri: "/",
      mainWindowRedirectUri: "/"
    });
  };

  const currentUser = accounts[0];

  // Check if user has access to an app based on group membership
  const hasAccessToApp = (app: App): boolean => {
    // If no required groups specified, allow access to everyone
    if (!app.requiredGroups || app.requiredGroups.length === 0) {
      return true;
    }
    
    // Check if user is in any of the required groups
    return app.requiredGroups.some(group => userGroups.includes(group));
  };

  const apps: App[] = [
    {
      id: 'port-reallocation',
      name: 'Port Reallocation Tool',
      description: 'Configure and manage portfolio reallocations across account managers',
      icon: '📊',
      path: '/port-reallocation/config',
      color: '#3498db',
      isExternal: false,
      // requiredGroups: ['Port-Reallocation-Users'] // DISABLED FOR DEV - Enable in production
    },
    {
      id: 'insurance-monitoring',
      name: 'Insurance Monitoring',
      description: 'Monitor and track insurance policies and coverage',
      icon: '🛡️',
      path: 'https://insurance.avidappdev.com/',
      color: '#27ae60',
      isExternal: true,
      // requiredGroups: ['Insurance-Monitoring-Users'] // DISABLED FOR DEV - Enable in production
    },
    {
      id: 'port-health',
      name: 'Port Health Monitoring',
      description: 'Monitor and analyze the health of account portfolios',
      icon: '💚',
      path: '/port-health',
      color: '#9b59b6',
      isExternal: false,
      // requiredGroups: [] // No groups required for dev
    },
    {
      id: 'legal-bankruptcy',
      name: 'Legal & Bankruptcy',
      description: 'View Legal accounts and total bankruptcy portfolio metrics',
      icon: '⚖️',
      path: '/legal-bankruptcy',
      color: '#2ecc71',
      isExternal: false,
      // requiredGroups: [] // No groups required for dev
    },
    {
      id: 'account-transfer',
      name: 'Account Transfer',
      description: 'Transfer loan accounts between account managers',
      icon: '🔄',
      path: '/account-transfer',
      color: '#f39c12',
      isExternal: false,
      // requiredGroups: [] // No groups required for dev
    }
  ];

  // Filter apps based on user's group membership
  const accessibleApps = apps.filter(hasAccessToApp);

  return (
    <div className="apps-landing">
      <div className="landing-header">
        <div className="user-info">
          <div className="user-avatar">{currentUser?.name?.charAt(0) || 'U'}</div>
          <div className="user-details">
            <div className="user-name">{currentUser?.name || 'User'}</div>
            <div className="user-email">{currentUser?.username || ''}</div>
          </div>
        </div>
        <button onClick={handleSignOut} className="sign-out-button">
          Sign Out
        </button>
      </div>

      <div className="landing-content">
        <h1 className="landing-title">Avid Acceptance Applications</h1>
        <p className="landing-subtitle">Select an application to get started</p>

        {!groupsLoaded ? (
          <div className="loading-message">
            <p>Loading your applications...</p>
          </div>
        ) : accessibleApps.length === 0 ? (
          <div className="no-access-message">
            <h3>No Applications Available</h3>
            <p>You don't have access to any applications at this time. Please contact your administrator if you believe this is an error.</p>
            <div className="debug-info">
              <details>
                <summary>Debug Information (for troubleshooting)</summary>
                <p><strong>Your Groups:</strong> {userGroups.length > 0 ? userGroups.join(', ') : 'None found'}</p>
                <p><strong>Available Apps:</strong> {apps.length}</p>
                <p><strong>Accessible Apps:</strong> {accessibleApps.length}</p>
              </details>
            </div>
          </div>
        ) : (
          <div className="apps-grid">
            {accessibleApps.map(app => (
            <div 
              key={app.id} 
              className="app-card"
              onClick={() => app.isExternal ? window.open(app.path, '_blank') : navigate(app.path)}
              style={{ borderColor: app.color }}
            >
              <div className="app-icon" style={{ backgroundColor: app.color }}>
                {app.icon}
              </div>
              <h3 className="app-name">{app.name}</h3>
              <p className="app-description">{app.description}</p>
              <button 
                className="app-launch-button"
                style={{ backgroundColor: app.color }}
              >
                Launch Application {app.isExternal ? '↗' : '→'}
              </button>
            </div>
            ))}
          </div>
        )}
      </div>

      <footer className="landing-footer">
        <p>&copy; 2025 Avid Acceptance. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default AppsLanding;
